import { Table, Badge, Pagination, Button, Checkbox } from "flowbite-react";
import { HiPencil, HiEye } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { Ticket, DataGridPagination, VectorizeStatusType, GraphStatusType } from "@/sharedTypes";

const vectorizeBadgeColor: Record<VectorizeStatusType, string> = {
  not_vectorized: "gray",
  pending: "warning",
  processing: "purple",
  vectorized: "success",
  failed: "failure",
};

const graphBadgeColor: Record<GraphStatusType, string> = {
  not_created: "gray",
  pending: "warning",
  processing: "purple",
  created: "success",
  failed: "failure",
};

interface Props {
  data: Ticket[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  onEdit: (ticket: Ticket) => void;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
}

export default function TicketAiTable({
  data,
  pagination,
  onPageChange,
  onEdit,
  selectedIds,
  onSelectionChange,
}: Props) {
  const { t } = useTranslation(["knowledgeBase", "common"]);

  const selectableData = data.filter(
    (ticket) => !ticket.is_ai_approved || ticket.vectorize_status !== "vectorized"
  );
  const allSelectableSelected =
    selectableData.length > 0 &&
    selectableData.every((ticket) => selectedIds.includes(ticket.ticket_id!));

  const handleSelectAll = () => {
    if (allSelectableSelected) {
      onSelectionChange(selectedIds.filter((id) => !selectableData.some((t) => t.ticket_id === id)));
    } else {
      const newIds = selectableData.map((t) => t.ticket_id!).filter((id) => !selectedIds.includes(id));
      onSelectionChange([...selectedIds, ...newIds]);
    }
  };

  const handleSelectOne = (ticketId: number) => {
    if (selectedIds.includes(ticketId)) {
      onSelectionChange(selectedIds.filter((id) => id !== ticketId));
    } else {
      onSelectionChange([...selectedIds, ticketId]);
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg shadow">
      <Table hoverable>
        <Table.Head>
          <Table.HeadCell className="w-10">
            {selectableData.length > 0 && (
              <Checkbox checked={allSelectableSelected} onChange={handleSelectAll} />
            )}
          </Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:ticketTitle")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:aiIssueText")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:aiSolutionText")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:aiApproved")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:vectorized")}</Table.HeadCell>
          {/* TODO: temporarily hidden restore tomorrow */}
          {/* <Table.HeadCell>{t("knowledgeBase:graphStatus")}</Table.HeadCell> */}
          <Table.HeadCell>{t("knowledgeBase:actions")}</Table.HeadCell>
        </Table.Head>
        <Table.Body className="divide-y">
          {data.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={7} className="text-center py-8 text-gray-500">
                {t("knowledgeBase:noResolvedTickets")}
              </Table.Cell>
            </Table.Row>
          ) : (
            data.map((ticket) => (
              <Table.Row
                key={ticket.ticket_id}
                className="bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                <Table.Cell className="w-10">
                  {(!ticket.is_ai_approved || ticket.vectorize_status !== "vectorized") && (
                    <Checkbox
                      checked={selectedIds.includes(ticket.ticket_id!)}
                      onChange={() => handleSelectOne(ticket.ticket_id!)}
                    />
                  )}
                </Table.Cell>
                <Table.Cell className="font-medium text-gray-900 dark:text-white">
                  {ticket.ticket_title}
                </Table.Cell>
                <Table.Cell className="max-w-xs truncate">
                  {ticket.ai_issue_text || <span className="text-gray-400 italic">{t("knowledgeBase:notFilled")}</span>}
                </Table.Cell>
                <Table.Cell className="max-w-xs truncate">
                  {ticket.ai_solution_text || <span className="text-gray-400 italic">{t("knowledgeBase:notFilled")}</span>}
                </Table.Cell>
                <Table.Cell>
                  <Badge color={ticket.is_ai_approved ? "success" : "gray"} size="sm">
                    {ticket.is_ai_approved ? t("knowledgeBase:yes") : t("knowledgeBase:no")}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={vectorizeBadgeColor[ticket.vectorize_status || "not_vectorized"]} size="sm">
                    {t(`knowledgeBase:vectorizeStatus.${ticket.vectorize_status || "not_vectorized"}`)}
                  </Badge>
                </Table.Cell>
                {/* TODO: temporarily hidden restore tomorrow */}
                {/* <Table.Cell>
                  <Badge color={graphBadgeColor[ticket.graph_status || "not_created"]} size="sm">
                    {t(`knowledgeBase:graphStatusValues.${ticket.graph_status || "not_created"}`)}
                  </Badge>
                </Table.Cell> */}
                <Table.Cell>
                  <Button size="xs" color="light" onClick={() => onEdit(ticket)}>
                    <HiPencil className="h-4 w-4" />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center py-4">
          <Pagination
            currentPage={pagination.pageIndex}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            showIcons
          />
        </div>
      )}
    </div>
  );
}
